import type { Map as ImmutableMap } from 'immutable';
import { DateTime } from 'luxon';

import type { ScheduledTransactionType } from 'app-components/flux/scheduled-transactions/scheduledTransactionsTypes';
import { getLogger, tracker } from 'app-components/utils/core';
import store from 'app-components/utils/redux-store';

import { getCanBVA } from './billPresentmentSelectors';
import type {
  Biller,
  BillerAccount,
  BillPresentmentSlice,
  EnrollmentErrorPayload,
  EnrollResponse,
  Field,
  FormField,
  FormParam,
  Parameter,
} from './billPresentmentTypes';

const log = getLogger('flux/bill-presentment/billPresentmentUtils');

/**
 * Biller (Fiserv) linking patches the series by id once enrollment succeeds, so
 * it is only possible for a saved series. BVA linking has no such requirement:
 * it only sets fields saved with the series. See SIMPL-30330.
 */
export function canLinkBiller(series?: Pick<ScheduledTransactionType, 'id'> | null): boolean {
  return Boolean(series?.id);
}

export function extractHostname(biller: Biller) {
  if (!biller) return '';

  const url = biller.url || '';

  let hostname;
  // find & remove protocol (http, ftp, etc.) and get hostname
  if (url.indexOf('//') > -1) {
    hostname = url.split('/')[2];
  } else {
    hostname = url.split('/')[0];
  }
  // find & remove any port number
  hostname = hostname.split(':')[0];
  // find & remove any query "?"
  hostname = hostname.split('?')[0];
  // find & remove any beginning www.
  if (hostname.indexOf('www.') === 0) {
    hostname = hostname.slice(4);
  }
  return hostname;
}

// # -      Field Classification
// # - - - - - - - - - - - - - - - - - - - - - - -

/**
 * Client-side categorization of enrollment fields. Enrollment schemas come from
 * the Malipo biller directory and field naming/labels vary per biller, so kinds
 * are derived from normalized name + label words rather than exact names only.
 */
export type BillFieldKind =
  | 'account-number'
  | 'phone'
  | 'email'
  | 'street-number'
  | 'street-name'
  | 'apartment'
  | 'city'
  | 'state'
  | 'zip'
  | 'date-of-birth'
  | 'text';

export type BillParameterKind = 'address' | 'accountholder' | 'other';

/** Lowercased words from a name or label, eg "Apt. / Unit (optional)" -> ['apt', 'unit', 'optional']. */
function tokenize(value?: string): string[] {
  return (value || '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

function wordsOf(item: Pick<Field, 'name' | 'label'>): Set<string> {
  return new Set([...tokenize(item.name), ...tokenize(item.label)]);
}

export function getFieldKind(field: Pick<Field, 'name' | 'label'>): BillFieldKind {
  const words = wordsOf(field);
  const has = (...tokens: string[]) => tokens.every((token) => words.has(token));
  if (has('birth') || has('dob')) return 'date-of-birth';
  if (has('phone')) return 'phone';
  if (has('email')) return 'email';
  if (has('zip') || has('postal')) return 'zip';
  if (has('state')) return 'state';
  if (has('apartment') || has('apt') || has('unit')) return 'apartment';
  if (has('city')) return 'city';
  // both words required so "Accountholder's First Name" and "Account Holder Name"
  // stay text and never get whitespace-stripped at submit
  if (has('account', 'number')) return 'account-number';
  if (has('street', 'number') || (words.size === 1 && words.has('number'))) return 'street-number';
  if (has('street')) return 'street-name';
  return 'text';
}

export function getParameterKind(parameter: Pick<Parameter, 'name' | 'label'>): BillParameterKind {
  const words = wordsOf(parameter);
  if (words.has('address')) return 'address';
  if (words.has('consumer') || words.has('consumers') || words.has('accountholder')) return 'accountholder';
  return 'other';
}

/**
 * Design-normalized labels for the fields whose presentation the client owns;
 * all other fields keep their server-provided label.
 */
const FIELD_KIND_LABELS: Partial<Record<BillFieldKind, string>> = {
  phone: 'Phone number',
  email: 'Email',
  'street-number': 'Number',
  'street-name': 'Street name',
  apartment: 'Apt. / Unit (optional)',
  state: 'State',
  zip: 'ZIP code',
  'date-of-birth': 'Date of birth',
};

const PARAMETER_KIND_LABELS: Partial<Record<BillParameterKind, string>> = {
  address: 'Address',
  accountholder: 'Accountholder',
};

export function getFieldDisplayLabel(field: Pick<Field, 'name' | 'label'>): string {
  return FIELD_KIND_LABELS[getFieldKind(field)] ?? field.label;
}

export function getParameterDisplayLabel(parameter: Pick<Parameter, 'name' | 'label'>): string {
  return PARAMETER_KIND_LABELS[getParameterKind(parameter)] ?? parameter.label;
}

// # -      Phone / digit helpers
// # - - - - - - - - - - - - - - - - - - - - - - -

export const US_PHONE_DIGITS = 10;
export const US_ZIP_DIGITS = 5;
const US_ZIP_PLUS4_DIGITS = 9;

/** Digits-only view of a value, eg "(810) 841-7877" -> "8108417877". */
export function stripToDigits(value: string): string {
  return value.replace(/\D+/g, '');
}

/**
 * Digits of a US phone number with the country code dropped, so unmasked
 * entries like "+1 (810) 841-7877" normalize to the 10 digits the service
 * expects.
 */
function normalizeUsPhoneDigits(value: string): string {
  const digits = stripToDigits(value);
  return digits.length === US_PHONE_DIGITS + 1 && digits.startsWith('1') ? digits.slice(1) : digits;
}

/**
 * Progressive US phone mask: digits flow into "(xxx) xxx-xxxx" as they are
 * typed. Separators only appear once the next digit exists so deleting
 * characters never fights the formatter.
 */
export function formatPhoneNumber(value: string): string {
  const digits = stripToDigits(value).slice(0, US_PHONE_DIGITS);
  if (!digits) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

// # -      Form Functions
// # - - - - - - - - - - - - - - - - - - - - - - -

export function initFormError(billerAccount: BillerAccount, series: ScheduledTransactionType) {
  if (
    billerAccount?.status === 'FAILED' ||
    (series.billPresentmentAccountId && series?.billerEnrollmentStatus === 'FAILED')
  ) {
    return (
      billerAccount.additionalInfo?.description ||
      'There was an error connecting to this biller. Please try again later.'
    );
  }
  return null;
}

function makeRegex(regexString: string) {
  let pattern = regexString;
  let flags = '';

  // if it’s wrapped in slashes, pull pattern & flags out
  const slashMatch = regexString.match(/^\/(.+)\/([a-z]*)$/i);
  if (slashMatch) {
    pattern = slashMatch[1];
    flags = slashMatch[2];
  }
  return new RegExp(pattern, flags);
}

export function initializeForm(enrollment?: EnrollResponse | null) {
  if (!enrollment) return [];
  return enrollment.parameters.map((param: Parameter) => ({
    ...param,
    fields: param.fields.map((field) => ({ ...field, value: '' })),
  }));
}

export const REQUIRED_FIELD_ERROR = 'Required field';
export const INVALID_PHONE_ERROR = 'Enter a valid phone number';
export const INVALID_EMAIL_ERROR = 'Enter a valid email address';
export const INVALID_ZIP_ERROR = 'Enter a valid ZIP code';
export const INVALID_DOB_ERROR = 'Enter a valid date of birth';

/**
 * Lax client-side email shape (server is source of truth): requires x@y.z so a
 * dot in the local part alone (eg "name.x@host") does not pass as valid.
 */
const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * Client-owned format rules, applied on top of whatever validation the
 * enrollment schema provides. Values are normalized first so
 * formatted entries (eg "(810) 841-7877") still pass.
 */
function getClientRuleError(field: FormField): string | null {
  switch (getFieldKind(field)) {
    case 'phone':
      return normalizeUsPhoneDigits(field.value).length === US_PHONE_DIGITS ? null : INVALID_PHONE_ERROR;
    case 'email':
      return EMAIL_PATTERN.test(field.value) ? null : INVALID_EMAIL_ERROR;
    case 'zip': {
      // web has no input mask, so ZIP+4 entries like "48104-1234" must pass too
      const zipDigits = stripToDigits(field.value).length;
      return zipDigits === US_ZIP_DIGITS || zipDigits === US_ZIP_PLUS4_DIGITS ? null : INVALID_ZIP_ERROR;
    }
    case 'date-of-birth': {
      const dob = parseDateOfBirth(field.value);
      return dob && dob <= DateTime.now().endOf('day') ? null : INVALID_DOB_ERROR;
    }
    default:
      return null;
  }
}

export function getFieldError(field: FormField) {
  const { validation, value } = field;
  // error pinned by applyEnrollmentFieldErrors; clears as soon as the user edits the value
  if (field.serverError?.message && field.serverError.value === value) {
    return field.serverError.message;
  }
  if (validation?.required && !value) {
    return REQUIRED_FIELD_ERROR;
  }
  // optional fields left empty never error (eg "Apt. / Unit (optional)")
  if (!value) {
    return null;
  }
  const clientRuleError = getClientRuleError(field);
  if (clientRuleError) {
    return clientRuleError;
  }
  if (validation) {
    if (validation.minLength && value.length < validation.minLength) {
      return `Must be at least ${validation.minLength} characters`;
    }
    if (validation.maxLength && value.length > validation.maxLength) {
      return `Must be less than ${validation.maxLength + 1} characters`;
    }
    if (validation.regex) {
      const reg = makeRegex(validation.regex);
      if (reg && !reg.exec(value)) {
        return 'Check format and try again';
      }
    }
  }
  return null;
}

export function hasFormError(formValues: FormParam[]) {
  return formValues.some((param) => param.fields.some(getFieldError));
}

// luxon fromFormat requires a whole-string match, an exactly-4-digit yyyy, and a
// real calendar date — so 2-digit years and dates like 02/30 fail and pass through as-is.
const DOB_INPUT_FORMATS = ['M/d/yyyy', 'M-d-yyyy', 'yyyy-M-d'];

function parseDateOfBirth(value: string): DateTime | null {
  const trimmed = value.trim();
  for (const format of DOB_INPUT_FORMATS) {
    const dt = DateTime.fromFormat(trimmed, format);
    if (dt.isValid) return dt;
  }
  return null;
}

function normalizeDateOfBirth(value: string): string {
  // unparseable: send as-is so the backend field error (now surfaced) explains it
  return parseDateOfBirth(value)?.toISODate() ?? value;
}

/** Per-kind submit normalization; display formatting (phone mask, spacing) never reaches the service. */
function makeSubmitValue(field: FormField): string {
  if (!field.value) return field.value;
  switch (getFieldKind(field)) {
    case 'date-of-birth':
      return normalizeDateOfBirth(field.value);
    case 'account-number':
      return field.value.replace(/\s/g, '') || field.value;
    case 'phone':
      return normalizeUsPhoneDigits(field.value) || field.value;
    default:
      return field.value;
  }
}

export function makeParametersFromFormValues(formValues: FormParam[]) {
  return formValues.map((param) => ({
    name: param.name,
    fields: param.fields.map((field) => ({
      name: field.name,
      value: makeSubmitValue(field),
    })),
  }));
}

// # -      Enrollment Error Functions
// # - - - - - - - - - - - - - - - - - - - - - - -

function humanizeFieldName(field: string): string {
  const words = field.replace(/[-_]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : field;
}

/** Normalizes an enrollment failure (axios error or already-normalized payload) into a serializable payload. */
export function toEnrollmentErrorPayload(e: unknown): EnrollmentErrorPayload {
  const err = e as EnrollmentErrorPayload & { response?: { data?: EnrollmentErrorPayload } };
  const data = err?.response?.data;
  return {
    title: data?.title ?? err?.title,
    detail: data?.detail ?? err?.detail,
    code: data?.code ?? err?.code,
    errorCode: data?.errorCode ?? err?.errorCode,
    fieldErrors: data?.fieldErrors ?? err?.fieldErrors,
    message: typeof err?.message === 'string' ? err.message : undefined,
  };
}

/** User-facing enrollment failure message. Used by web LoginView and mobile useBillLogin. */
export function getEnrollmentErrorMessage(e: unknown): string {
  const { title, detail, fieldErrors, message } = toEnrollmentErrorPayload(e);
  const fieldMessages = (Array.isArray(fieldErrors) ? fieldErrors : [])
    .filter((fe) => fe?.message)
    .map((fe) => (fe.field ? `${humanizeFieldName(fe.field)}: ${fe.message}` : fe.message));
  if (fieldMessages.length) return fieldMessages.join('; ');
  if (title && detail) return `${title} - ${detail}`;
  if (message) return `Something went wrong - ${message} - please check your fields and try again`;
  return 'Something went wrong - please check your fields and try again';
}

/**
 * Pins the service's field errors onto the matching form fields so getFieldError
 * surfaces them inline, exactly like client-side validation. Returns the updated
 * form plus a message covering whatever could not be shown inline — field errors
 * that matched no form field, or failures with no field errors at all. A null
 * errorMessage means every error is displayed on a field.
 */
export function applyEnrollmentFieldErrors(formValues: FormParam[], e: unknown) {
  const payload = toEnrollmentErrorPayload(e);
  const fieldErrors = (Array.isArray(payload.fieldErrors) ? payload.fieldErrors : []).filter(
    (fe) => fe?.field && fe?.message,
  );
  if (!fieldErrors.length) {
    return { formValues, errorMessage: getEnrollmentErrorMessage(e) };
  }

  const matchedFields = new Set<string>();
  const nextFormValues = formValues.map((param) => ({
    ...param,
    fields: param.fields.map((field) => {
      const fieldError = fieldErrors.find((fe) => fe.field === field.name);
      if (!fieldError) return field;
      matchedFields.add(fieldError.field);
      return { ...field, serverError: { message: fieldError.message, value: field.value } };
    }),
  }));

  const unmatched = fieldErrors.filter((fe) => !matchedFields.has(fe.field));
  return {
    formValues: nextFormValues,
    errorMessage: unmatched.length ? getEnrollmentErrorMessage({ ...payload, fieldErrors: unmatched }) : null,
  };
}

// # -      Tracker Functions
// # - - - - - - - - - - - - - - - - - - - - - - -

export function trackFiServModels(
  scheduledTxns?: ImmutableMap<string, ScheduledTransactionType> | ScheduledTransactionType[] | null,
) {
  let pastBillgoBillsPresent = 0;
  let pendingFiservAccounts = 0;
  let connectedFiservAccounts = 0;
  let billedFiservAccounts = 0;
  let failedFiservAccounts = 0;
  let bvaBillsPresent = 0;
  try {
    const state = store?.getState?.();
    scheduledTxns?.forEach((model) => {
      switch (true) {
        case state && getCanBVA(state, model):
          bvaBillsPresent += 1;
          break;
        case !!model?.presentmentAccountId:
          if (model.billerEnrollmentStatus === 'CONNECTED') {
            connectedFiservAccounts += 1;
            if (model.billSnapshot) {
              billedFiservAccounts += 1;
            }
          } else if (model.billerEnrollmentStatus === 'PENDING') {
            pendingFiservAccounts += 1;
          } else {
            failedFiservAccounts += 1;
          }
          break;
        case !!model?.billPresentmentAccountId:
          pastBillgoBillsPresent += 1;
      }
    });
  } catch (e) {
    log.warn('error while tracking fiserv migration', e);
  }
  tracker.track(tracker.events.launchQuicken, {
    pastBillgoBillsPresent,
    pendingFiservAccounts,
    connectedFiservAccounts,
    failedFiservAccounts,
    bvaBillsPresent,
    billedFiservAccounts,
  });
}

export function trackFormEnter(formValues: FormParam[], hasError?: boolean) {
  const errorFields: string[] = [];
  const errorDescriptions: string[] = [];
  try {
    if (hasError) {
      formValues.forEach((param) => {
        param.fields.forEach((field) => {
          const fieldError = getFieldError(field);
          if (fieldError) {
            errorFields.push(field.name);
            errorDescriptions.push(fieldError);
          }
        });
      });
    }
  } catch (e) {
    log.warn('error while tracking fiserv form entry', e);
  }
  tracker.track(tracker.events.enterDetails, { validationSuccess: !hasError, errorFields, errorDescriptions });
}

export function trackBillerAccountChanges(state: BillPresentmentSlice, payload: BillerAccount[]) {
  try {
    payload.forEach((account) => {
      const currentAccount = state.billerAccounts.entities[account.id];
      if (currentAccount && currentAccount.status !== account.status) {
        const biller = state.billers.entities[account.billerId];
        const type = biller?.activation === 'REAL_TIME' ? 'fiservRealTime' : 'fiservBatch';
        tracker.track(tracker.events.fiservBillStatusChange, {
          billerName: biller?.name,
          type,
          billerId: account.billerId,
          billerAccountId: account.id,
          oldStatus: currentAccount.status,
          newStatus: account.status,
          reasonCode: account.additionalInfo?.code,
          reasonDescription: account.additionalInfo?.description,
          reasonErrorCode: account.additionalInfo?.details?.[0]?.errorCode,
          reasonDetails: account.additionalInfo?.details,
        });
        if (account.status === 'CONNECTED') {
          tracker.track(tracker.events.fiservBillSuccess, {
            billerName: biller?.name,
            type,
          });
        } else if (account.status === 'FAILED' || account.status === 'DISCONNECTED') {
          tracker.track(tracker.events.fiservBillFailed, {
            billerName: biller?.name,
            type,
            reasonCode: account.additionalInfo?.code,
            reasonDescription: account.additionalInfo?.description,
            reasonErrorCode: account.additionalInfo?.details?.[0]?.errorCode,
            reasonDetails: account.additionalInfo?.details,
          });
        }
      }
    });
  } catch (e) {
    log.warn('error while tracking fiserv account retrieval changes', e);
  }
}
